<template>
  <div class="substep-set-payment-details">
    <div class="substep-header">
      <h3 data-testId="step-title">Set member payment details</h3>
      <p>
        Set pay rates and pay periods for your team. You can also set timesheets to require approval before payments are
        sent.
      </p>
    </div>
    <div class="substep-body">
      <AppTable
        :table-data="members"
        class="payment-details-table"
        :loading="showLoading"
      >
        <AppTableColumn
          v-slot="{ row }"
          cell-classes="text-left col-email"
          header-classes="text-left col-email"
          header="Email"
        >
          <span>{{ row.email }}</span>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Payment details"
          cell-classes="text-right col-payment-details"
          header-classes="text-right col-payment-details"
        >
          <div v-if="row.pay_rate == null">
            <span class="details-not-set">Details not set</span>
          </div>
          <div v-else>
            <span>Pay rate: {{ formatPayRate(row.pay_rate) }}</span>
            <span :data-testid="`payment-details-frequency-${row.id}`">{{ getPayPeriod(row.pay_period) }}</span>
            <span
              v-if="getPeriodStart(row)"
              :data-testid="`payment-details-period-start-${row.id}`"
            >{{ getPeriodStart(row) }}</span>
          </div>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          cell-classes="text-right"
        >
          <AppButton
            v-if="!row.pay_rate"
            class="btn"
            :data-testid="`set-payment-details-${row.id}`"
            @click="editMember(row)"
          >
            Set details
          </AppButton>
          <AppButton
            v-else
            disabled
            class="btn btn-outline-success disabled"
          >
            Details set
            <i class="hi hi-check" />
          </AppButton>
        </AppTableColumn>
      </AppTable>

      <AppDialog
        class="getting-started-dialog-payment-details"
        data-testid="getting-started-payment-details-dialog"
        position="centered"
        append-to-body
        title="Edit Payment Details"
        :show="showDialog"
        @close="toggleDialog()"
      >
        <div class="form">
          <div class="form-group">
            <label class="control-label">Member</label>
            <span>{{ selectedMember.email }}</span>
          </div>
          <div :class="{ 'form-group': true, 'has-error': errorMessages['pay_rate'] }">
            <label class="control-label">
              <div class="text">
                Pay Rate&nbsp;
                <i
                  v-tooltip
                  class="hi hi-info-filled"
                  data-original-title="This is your member's hourly pay rate"
                />
              </div>
            </label>
            <div class="control-input">
              <input
                v-model="selectedMember.pay_rate"
                class="form-control numeric decimal optional"
                type="number"
                name="payRate"
                data-testid="payment-details-pay-rate-input"
              />
              <p class="help-block">USD/hr</p>
              <p class="error help-block">{{ errorMessages['pay_rate']?.join(', ') }}</p>
            </div>
          </div>
          <div
            v-if="!hasCustomPayPeriodDates"
            class="form-group"
          >
            <label class="control-label">
              <div class="text">
                Pay Period&nbsp;
                <i
                  v-tooltip
                  class="hi hi-info-filled"
                  data-original-title="This is how often your member gets paid"
                />
              </div>
            </label>
            <AppSelect
              v-model="selectedMember.pay_period"
              :select2-options="{ width: '100%' }"
              :options="payPeriodOptions"
            />
          </div>
          <PayPeriodCustomDatesSelector
            v-else
            :member="selectedMember"
            :member-initial-state="selectedMemberInitialState"
            :pay-period-options="customPayPeriodOptions"
            @input="onCustomPayPeriodSelectorInput"
          />
          <template v-if="timesheetApprovalsAvailable">
            <div class="form-group">
              <label class="control-label">&nbsp;</label>
              <div class="control-input">
                <AppSwitch
                  v-model="selectedMember.timesheets_enabled"
                  name="requireApproval"
                  class="title-switch cursor-pointer"
                  label="Require timesheet approval"
                  text-position="right"
                />
                <i
                  v-tooltip
                  class="hi hi-info-filled"
                  data-original-title="When this is enabled, timesheets need to be approved before payments go out to your member"
                />
              </div>
            </div>
          </template>
        </div>
        <template slot="footer">
          <AppButton
            class="btn"
            @click="toggleDialog()"
          >
            Cancel
          </AppButton>
          <AppButton
            class="btn btn-primary"
            data-testid="save-payment-details-button"
            @click="updatePaymentDetails()"
          >
            Save
          </AppButton>
        </template>
      </AppDialog>
    </div>
  </div>
</template>

<script>
import { pick } from 'lodash-es';
import {
  BIWEEKLY_PAY_PERIOD,
  MEMBERSHIP_PAY_PERIOD_OPTIONS,
  MONTHLY_PAY_PERIOD,
  PAY_PERIOD_DAY_OF_MONTH_OPTIONS,
  PAY_PERIOD_DAY_OF_WEEK_OPTIONS,
  TWICE_PER_MONTH_PAY_PERIOD,
  WEEKLY_PAY_PERIOD,
} from '~/app_constants';
import AppButton from '~/components/AppButton.vue';
import AppDialog from '~/components/AppDialog.vue';
import AppSelect from '~/components/AppSelect.vue';
import AppSwitch from '~/components/AppSwitch.vue';
import AppTable from '~/components/AppTable.vue';
import PayPeriodCustomDatesSelector from '~/components/shared/PayPeriodCustomDatesSelector.vue';
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import { InvitesResource, UserOrganizationsResource } from '~/resources';
import { validatePayPeriodState } from '~/utils/validate_pay_period_state';

const PERIOD_FIELDS = ['period_week_day', 'period_start_day', 'period_second_start_day', 'period_anchor_date'];

export default {
  name: 'SetMemberPaymentDetails',
  components: {
    AppButton,
    AppDialog,
    AppSelect,
    AppSwitch,
    AppTable,
    PayPeriodCustomDatesSelector,
  },
  mixins: [GettingStartedMixin],
  props: {
    organization: {
      type: Object,
      required: true,
      validator: (organization) => {
        return !!organization.id;
      },
    },
    users: {
      type: Array,
      default: () => [],
      validator: (users) => {
        return users.every((user) => {
          return !!user.id && !!user.email;
        });
      },
    },
    invites: {
      type: Array,
      default: () => [],
      validator: (invites) => {
        return invites.every((invite) => {
          return !!invite.id && !!invite.email;
        });
      },
    },
    timesheetApprovalsAvailable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    errorMessages: {},
    showLoading: false,
    selectedMember: {},
    selectedMemberInitialState: {},
    showDialog: false,
  }),
  computed: {
    hasCustomPayPeriodDates() {
      return !!this.organization.custom_pay_period_dates_enabled;
    },
    customPayPeriodOptions() {
      return MEMBERSHIP_PAY_PERIOD_OPTIONS.map((o) => ({ id: o.id, text: o.text }));
    },
    members() {
      return this.users
        .map((user) => {
          return {
            ...user,
            type: 'user',
          };
        })
        .concat(
          this.invites.map((invite) => {
            return {
              ...invite,
              type: 'invite',
            };
          }),
        );
    },
    payPeriodOptions() {
      return [
        { id: '', text: 'None' },
        { id: 0, text: 'Weekly' },
        { id: 1, text: 'Twice per month' },
        { id: 2, text: 'Bi-weekly' },
        { id: 3, text: 'Monthly' },
      ];
    },
  },
  watch: {
    members: {
      handler() {
        this.checkPaymentDetails();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    toggleDialog() {
      this.showDialog = !this.showDialog;
      this.errorMessages = {};
    },
    getPayPeriod(payPeriodId) {
      const option = this.payPeriodOptions.find((option) => option.id === payPeriodId);

      if (option) {
        return option.text;
      }

      return '';
    },
    editMember(member) {
      this.selectedMember = { ...member };
      this.selectedMemberInitialState = { ...member };
      this.errorMessages = {};
      this.showDialog = true;
    },
    onCustomPayPeriodSelectorInput(nextState) {
      this.selectedMember = {
        ...this.selectedMember,
        pay_period: nextState.payPeriodId !== '' ? Number(nextState.payPeriodId) : '',
        _previousPayPeriodId: nextState.previousPayPeriodId,
        period_week_day: nextState.periodWeekDay,
        period_start_day: nextState.periodStartDay,
        period_second_start_day: nextState.periodSecondStartDay,
        period_anchor_date: nextState.periodAnchorDate,
      };
    },
    getPeriodStart(member) {
      const payPeriod = member.pay_period != null ? String(member.pay_period) : null;

      switch (payPeriod) {
        case WEEKLY_PAY_PERIOD:
        case BIWEEKLY_PAY_PERIOD: {
          const day = PAY_PERIOD_DAY_OF_WEEK_OPTIONS.find((d) => d.value === member.period_week_day);

          return day ? `Every ${day.name}` : null;
        }
        case MONTHLY_PAY_PERIOD: {
          const day = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((d) => d.value === member.period_start_day);

          return day ? `Starts ${day.text}` : null;
        }
        case TWICE_PER_MONTH_PAY_PERIOD: {
          const start = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((d) => d.value === member.period_start_day);
          const end = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((d) => d.value === member.period_second_start_day);

          return start && end ? `${start.text} and ${end.text}` : null;
        }
        default:
          return null;
      }
    },
    formatPayRate(payRate) {
      return payRate ? `$${parseFloat(payRate).toFixed(2)}` : 'Not set';
    },
    async updateInvite() {
      const payload = {
        pay_rate: this.selectedMember.pay_rate ? parseFloat(this.selectedMember.pay_rate) : null,
        pay_period: this.selectedMember.pay_period,
        timesheets_enabled: this.timesheetApprovalsAvailable && this.selectedMember.timesheets_enabled ? 1 : 0,
        ...(this.hasCustomPayPeriodDates ? pick(this.selectedMember, PERIOD_FIELDS) : {}),
      };

      return await InvitesResource.updatePayRate(
        { id: this.selectedMember.id },
        payload,
      );
    },
    async updateUser() {
      const userOrgPayload = {
        pay_rate: this.selectedMember.pay_rate,
        pay_period: this.selectedMember.pay_period,
        timesheets_enabled: this.timesheetApprovalsAvailable && this.selectedMember.timesheets_enabled,
        ...(this.hasCustomPayPeriodDates ? pick(this.selectedMember, PERIOD_FIELDS) : {}),
      };

      return await UserOrganizationsResource.updatePaymentInfo(
        { id: this.selectedMember.id },
        { user_organization: userOrgPayload },
      );
    },
    checkPaymentDetails() {
      const hasPayRate = this.members.some((member) => member.pay_rate);

      this.$emit(hasPayRate ? 'completeSubstep' : 'incompleteSubstep');
    },
    validateCustomPayPeriodSelection() {
      if (!this.hasCustomPayPeriodDates) {
        return true;
      }

      const message = validatePayPeriodState({
        payPeriodId: this.selectedMember.pay_period,
        periodWeekDay: this.selectedMember.period_week_day,
        periodStartDay: this.selectedMember.period_start_day,
        periodSecondStartDay: this.selectedMember.period_second_start_day,
        periodAnchorDate: this.selectedMember.period_anchor_date,
      });

      if (!message) {
        return true;
      }

      this.$appMessage(message);

      return false;
    },
    async updatePaymentDetails() {
      if (!this.validateCustomPayPeriodSelection()) {
        return;
      }

      this.showLoading = true;
      try {
        let response;

        if (this.selectedMember.type === 'invite') {
          response = await this.updateInvite();
        } else {
          response = await this.updateUser();
        }

        if (response.success) {
          this.$emit('reloadSubstep');
          this.showDialog = false;
        } else {
          this.errorMessages = response.errors;
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.showLoading = false;
      }
    },
  },
};
</script>
